export const INSTALL_PROMPT_STORAGE_VERSION = 1;
export const INSTALL_PROMPT_DAY_MS = 24 * 60 * 60 * 1_000;
export const INSTALL_PROMPT_COOLDOWN_DAYS = 30;
export const INSTALL_PROMPT_COOLDOWN_MS =
  INSTALL_PROMPT_COOLDOWN_DAYS * INSTALL_PROMPT_DAY_MS;
export const INSTALL_PROMPT_STORAGE_KEY =
  "whitehaven:preferences:install-promotion-v1";

export type InstallPromptStatus =
  | "idle"
  | "eligible"
  | "manual"
  | "prompting"
  | "deferred"
  | "accepted"
  | "installed"
  | "error";

export type InstallPromptMode = "browser" | "ios" | null;
export type InstallPromptAction = "install" | "instructions" | "dismiss" | null;
export type InstallPromptCode = "PROMPT_FAILED" | null;

export interface InstallPromptSnapshot {
  readonly status: InstallPromptStatus;
  readonly mode: InstallPromptMode;
  readonly action: InstallPromptAction;
  readonly code: InstallPromptCode;
}

export interface InstallPromptNavigator {
  readonly maxTouchPoints?: number;
  readonly platform?: string;
  readonly standalone?: boolean;
  readonly userAgent?: string;
}

export const IDLE_INSTALL_PROMPT: InstallPromptSnapshot = Object.freeze({
  status: "idle",
  mode: null,
  action: null,
  code: null,
});

export function createInstallPromptSnapshot(
  status: InstallPromptStatus,
  options: Partial<Omit<InstallPromptSnapshot, "status">> = {},
): InstallPromptSnapshot {
  return Object.freeze({
    status,
    mode: options.mode ?? null,
    action: options.action ?? null,
    code: options.code ?? null,
  });
}

export function shouldShowInstallPrompt(
  snapshot: InstallPromptSnapshot,
): boolean {
  return ["eligible", "manual", "prompting", "error"].includes(snapshot.status);
}

export function isIosInstallEnvironment(
  navigatorState: InstallPromptNavigator | null,
): boolean {
  if (!navigatorState) {
    return false;
  }

  try {
    const platform = navigatorState.platform ?? "";
    const userAgent = navigatorState.userAgent ?? "";
    return (
      /iPad|iPhone|iPod/i.test(platform) ||
      /iPad|iPhone|iPod/i.test(userAgent) ||
      (platform === "MacIntel" && (navigatorState.maxTouchPoints ?? 0) > 1)
    );
  } catch {
    return false;
  }
}

export function serializeInstallPromptCooldown(now: number): string {
  const currentTime = Number.isFinite(now) ? now : 0;
  return JSON.stringify({
    version: INSTALL_PROMPT_STORAGE_VERSION,
    nextEligibleDay: Math.floor(
      (currentTime + INSTALL_PROMPT_COOLDOWN_MS) / INSTALL_PROMPT_DAY_MS,
    ),
  });
}

export function parseInstallPromptCooldown(
  value: string | null,
  now: number,
): number | null {
  if (value === null || !Number.isFinite(now)) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(value);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return null;
    }

    const record = parsed as Record<string, unknown>;
    const keys = Object.keys(record);
    const currentDay = Math.floor(now / INSTALL_PROMPT_DAY_MS);
    if (
      keys.length !== 2 ||
      !keys.every((key) => ["version", "nextEligibleDay"].includes(key)) ||
      record.version !== INSTALL_PROMPT_STORAGE_VERSION ||
      typeof record.nextEligibleDay !== "number" ||
      !Number.isSafeInteger(record.nextEligibleDay) ||
      record.nextEligibleDay <= currentDay ||
      record.nextEligibleDay > currentDay + INSTALL_PROMPT_COOLDOWN_DAYS
    ) {
      return null;
    }

    return record.nextEligibleDay;
  } catch {
    return null;
  }
}
